import React, { useEffect, useRef } from 'react';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600&display=swap');

  .ai-anim-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Sora', sans-serif;
    overflow: hidden;
   
  }

  .ai-scene {
    width: 520px;
    height: 480px;
    position: relative;
  }

  .ai-node {
    position: absolute;
    background: #faf6f0;
    border-radius: 14px;
    padding: 14px 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.25);
    opacity: 0;
    transform: translateY(10px) scale(0.96);
    transition: opacity 0.5s ease, transform 0.5s ease;
    min-width: 180px;
  }
  .ai-node.ai-visible {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .ai-node-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .ai-node-title {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 14px;
    font-weight: 600;
    color: #2e2925;
  }
  .ai-node-title svg {
    color: #c87941;
  }
  .ai-node-badge {
    font-size: 10px;
    background: #ede8e0;
    color: #7a6955;
    padding: 2px 8px;
    border-radius: 20px;
    font-weight: 500;
  }
  .ai-node-divider {
    height: 1px;
    background: #e8e0d4;
    margin-bottom: 8px;
  }
  .ai-node-text {
    font-size: 12px;
    color: #5a4d40;
    line-height: 1.5;
    min-height: 20px;
  }

  .ai-cursor {
    display: inline-block;
    width: 1px;
    height: 13px;
    background: #c87941;
    margin-left: 1px;
    vertical-align: middle;
    animation: ai-blink 0.7s steps(1) infinite;
  }
  @keyframes ai-blink { 0%,100%{opacity:1} 50%{opacity:0} }

  .ai-arrow-svg {
    position: absolute;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  .ai-arrow-svg.ai-visible { opacity: 1; }

  .ai-conversation-tag {
    position: absolute;
    bottom: 145px;
    left: 185px;
    background: #ede8e0;
    color: #7a6955;
    font-size: 11px;
    padding: 5px 14px;
    border-radius: 20px;
    opacity: 0;
    transform: translateY(6px);
    transition: opacity 0.4s ease, transform 0.4s ease;
  }
  .ai-conversation-tag.ai-visible { opacity: 1; transform: translateY(0); }

  .ai-phase2 {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    opacity: 0;
    transition: opacity 0.6s ease;
    pointer-events: none;
  }
  .ai-phase2.ai-visible { opacity: 1; pointer-events: all; }

  .ai-tech-panel {
    position: absolute;
    left: 50%;
    top: 15px;
    transform: translateX(-50%);
    width: 190px;
    height: 430px;
    background: #faf6f0;
    border-radius: 20px;
    border: 1.5px solid #c87941;
    padding: 14px;
    box-shadow: 3px 3px 0px 0px #ADA9A3;
  }
  .ai-tech-panel-label {
    display: inline-block;
    border: 1px solid #c87941;
    color: #c87941;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 20px;
    margin-bottom: 18px;
    font-weight: 500;
  }

  .ai-sparkle {
    position: absolute;
    color: #c87941;
    font-size: 18px;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  .ai-sparkle.ai-visible { opacity: 1; }
  .ai-sp1 { top: 5px; right: 55px; }
  .ai-sp2 { top: 18px; right: 40px; font-size: 12px; }

  .ai-sub-card {
    background: #fff;
    border-radius: 12px;
    padding: 10px 12px;
    margin-bottom: 14px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.08);
    position: absolute;
    width: 165px;
    opacity: 0;
    transform: translateX(12px);
    transition: opacity 0.5s ease, transform 0.5s ease;
  }
  .ai-sub-card.ai-visible { opacity: 1; transform: translateX(0); }
  .ai-sub-card-title { font-size: 13px; font-weight: 600; color: #2e2925; margin-bottom: 6px; }
  .ai-sub-card-lines span {
    display: block;
    height: 3px;
    background: #ede8e0;
    border-radius: 4px;
    margin-bottom: 4px;
  }
  .ai-sub-card-lines span:last-child { width: 70%; }

  .ai-replay-btn {
    position: absolute;
    bottom: 18px;
    right: 18px;
    background: #c87941;
    color: #faf6f0;
    border: none;
    border-radius: 30px;
    padding: 7px 18px;
    font-size: 11px;
    font-family: 'Sora', sans-serif;
    font-weight: 600;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.4s ease, background 0.2s;
    letter-spacing: 0.04em;
  }
  .ai-replay-btn:hover { background: #a8622e; }
  .ai-replay-btn.ai-visible { opacity: 1; }
`;

const inputText = "I need to remotely monitor a bridge with inclinometer and vibration sensors.";
const outputText = "Perfect, so your goal is to control the tilt and vibrations of a bridge. Is that correct?";

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms));
}

async function typeText(el, text, speed = 38) {
  for (let i = 0; i < text.length; i++) {
    const cursor = el.querySelector('.ai-cursor');
    if (cursor) el.removeChild(cursor);
    el.textContent = text.slice(0, i + 1);
    const c = document.createElement('span');
    c.className = 'ai-cursor';
    el.appendChild(c);
    await sleep(speed + Math.random() * 20);
  }
  const cursor = el.querySelector('.ai-cursor');
  if (cursor) el.removeChild(cursor);
}

function show(el) {
  if (el) el.classList.add('ai-visible');
}

export default function AIAnimation2( { onComplete }) {
  const sceneRef = useRef(null);
  const runningRef = useRef(false);
  const cancelledRef = useRef(false);

  async function runAnimation() {
    if (runningRef.current) return;
    runningRef.current = true;
    cancelledRef.current = false;

    const scene = sceneRef.current;
    if (!scene) return;

    const s = (ms) => new Promise(r => {
      const id = setTimeout(r, ms);
      // store nothing, cancellation is via cancelledRef check
    });

    const checkCancelled = () => cancelledRef.current;

    // Reset all
    scene.querySelectorAll('.ai-visible').forEach(e => e.classList.remove('ai-visible'));
    const phase1 = scene.querySelector('#ai-phase1');
    const typingText = scene.querySelector('#ai-typing-text');
    const outputTextEl = scene.querySelector('#ai-output-text');
    if (typingText) {
      typingText.innerHTML = '<span class="ai-cursor"></span>';
    }
    if (outputTextEl) outputTextEl.textContent = '';
    if (phase1) { phase1.style.transition = ''; phase1.style.opacity = '1'; }

    await s(400); if (checkCancelled()) return;

    // 1. Text Input node
    show(scene.querySelector('#ai-node-text-input'));
    await s(700); if (checkCancelled()) return;

    // 2. Type
    if (typingText) await typeText(typingText, inputText, 35);
    if (checkCancelled()) return;
    await s(500); if (checkCancelled()) return;

    // 3. File Input
    show(scene.querySelector('#ai-node-file-input'));
    await s(400); if (checkCancelled()) return;

    // 4. Arrows
    show(scene.querySelector('#ai-arrow1'));
    show(scene.querySelector('#ai-arrow2'));
    await s(300); if (checkCancelled()) return;

    // 5. Text Output
    show(scene.querySelector('#ai-node-text-output'));
    await s(500); if (checkCancelled()) return;

    // 6. Output text
    if (outputTextEl) await typeText(outputTextEl, outputText, 28);
    if (checkCancelled()) return;
    await s(400); if (checkCancelled()) return;

    // 7. Conversation tag
    show(scene.querySelector('#ai-conv-arrow'));
    show(scene.querySelector('#ai-conversation-tag'));
    await s(1200); if (checkCancelled()) return;

    // 8. Fade out phase1
    if (phase1) {
      phase1.style.transition = 'opacity 0.7s ease';
      phase1.style.opacity = '0';
    }
    await s(500); if (checkCancelled()) return;

    // 9. Phase 2
    show(scene.querySelector('#ai-phase2'));
    show(scene.querySelector('#ai-sp1'));
    show(scene.querySelector('#ai-sp2'));
    await s(500); if (checkCancelled()) return;

    // 10. Cards
    show(scene.querySelector('#ai-card-sensors'));
    await s(600); if (checkCancelled()) return;
    show(scene.querySelector('#ai-card-vendors'));
    await s(600); if (checkCancelled()) return;
    show(scene.querySelector('#ai-card-prices'));
    await s(800); if (checkCancelled()) return;

 
  
   runningRef.current = false;
onComplete && onComplete();
  }

  useEffect(() => {
    runAnimation();
    return () => {
      cancelledRef.current = true;
      runningRef.current = false;
    };
  }, []);

  function handleReplay() {
    cancelledRef.current = true;
    runningRef.current = false;
    setTimeout(() => runAnimation(), 50);
  }

  return (
    <>
      <style>{styles}</style>
      <div className="ai-anim-wrapper">
        <div className="ai-scene" ref={sceneRef}>

          {/* PHASE 1 */}
          <div id="ai-phase1">

            {/* Text Input Node */}
            <div className="ai-node" id="ai-node-text-input" style={{ top: 20, left: 20, width: 220 }}>
              <div className="ai-node-header">
                <div className="ai-node-title">
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M4 6h16M4 12h8m-8 6h16" />
                  </svg>
                  Text Input
                </div>
                <span className="ai-node-badge">User Input</span>
              </div>
              <div className="ai-node-divider" />
              <div className="ai-node-text" id="ai-typing-text">
                <span className="ai-cursor" />
              </div>
            </div>

            {/* File Input Node */}
            <div className="ai-node" id="ai-node-file-input" style={{ top: 20, right: 20, width: 140 }}>
              <div className="ai-node-header">
                <div className="ai-node-title">
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  File Input
                </div>
              </div>
              <span className="ai-node-badge" style={{ display: 'inline-block', marginTop: 2 }}>User Input</span>
            </div>

            {/* Arrow 1 */}
            <svg className="ai-arrow-svg" id="ai-arrow1" viewBox="0 0 30 45"
              style={{ top: 120, left: 135, width: 30, height: 45 }}>
              <defs>
                <marker id="ah1" markerWidth="7" markerHeight="7" refX="3" refY="3.5" orient="auto">
                  <polygon points="0 0, 7 3.5, 0 7" fill="#c87941" />
                </marker>
              </defs>
              <line x1="15" y1="2" x2="15" y2="38" stroke="#c87941" strokeWidth="1.5" markerEnd="url(#ah1)" />
            </svg>

            {/* Arrow 2 */}
            <svg className="ai-arrow-svg" id="ai-arrow2" viewBox="0 0 60 75"
              style={{ top: 90, right: 55, width: 70, height: 85 }}>
              <defs>
                <marker id="ah2" markerWidth="7" markerHeight="7" refX="3" refY="3.5" orient="auto">
                  <polygon points="0 0, 7 3.5, 0 7" fill="#c87941" />
                </marker>
              </defs>
              <path d="M55 5 Q55 60 15 68" fill="none" stroke="#c87941" strokeWidth="1.5" markerEnd="url(#ah2)" />
            </svg>

            {/* Text Output Node */}
            <div className="ai-node" id="ai-node-text-output" style={{ top: 160, left: 80, width: 260 }}>
              <div className="ai-node-header">
                <div className="ai-node-title">
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M4 6h16M4 12h8m-8 6h16" />
                  </svg>
                  Text Output
                </div>
                <span className="ai-node-badge">Output</span>
              </div>
              <div className="ai-node-divider" />
              <div className="ai-node-text" id="ai-output-text" />
            </div>

            {/* Conv Arrow */}
            <svg className="ai-arrow-svg" id="ai-conv-arrow" viewBox="0 0 20 35"
              style={{ top: 290, left: 195, width: 20, height: 35 }}>
              <defs>
                <marker id="ah3" markerWidth="7" markerHeight="7" refX="3" refY="3.5" orient="auto">
                  <polygon points="0 0, 7 3.5, 0 7" fill="#c87941" />
                </marker>
              </defs>
              <line x1="10" y1="2" x2="10" y2="28" stroke="#c87941" strokeWidth="1.5" markerEnd="url(#ah3)" />
            </svg>

            <div className="ai-conversation-tag" id="ai-conversation-tag">Conversation...</div>
          </div>

          {/* PHASE 2 */}
          <div className="ai-phase2" id="ai-phase2">
            <div className="ai-tech-panel">
              <div className="ai-tech-panel-label">Technical specification</div>
            </div>
            <span className="ai-sparkle ai-sp1" id="ai-sp1">✦</span>
            <span className="ai-sparkle ai-sp2" id="ai-sp2">✦</span>

            <div className="ai-sub-card" id="ai-card-sensors" style={{ top: 75, left: 175 }}>
              <div className="ai-sub-card-title">Sensors</div>
              <div className="ai-sub-card-lines">
                <span /><span /><span />
              </div>
            </div>
            <div className="ai-sub-card" id="ai-card-vendors" style={{ top: 200, left: 230 }}>
              <div className="ai-sub-card-title">Vendors</div>
              <div className="ai-sub-card-lines">
                <span /><span /><span />
              </div>
            </div>
            <div className="ai-sub-card" id="ai-card-prices" style={{ top: 325, left: 160 }}>
              <div className="ai-sub-card-title">Prices</div>
              <div className="ai-sub-card-lines">
                <span /><span /><span />
              </div>
            </div>
          </div>

        

        </div>
      </div>
    </>
  );
}